import type { EEGTileTransportMode } from "../services/EEGSegmentCache";
import { alignTileWindowToBackend } from "../utils/tileWindowAlignment";

export function shouldKeepEEGViewerProtectedUltraRapidRunway(params: {
  speed: number;
  transportMode: EEGTileTransportMode;
  warmVisibleTransfer: boolean;
}): boolean {
  return resolveEEGViewerProtectedUltraRapidRunwayWindowCount(params) > 0;
}

export function resolveEEGViewerProtectedUltraRapidRunwayWindowCount(params: {
  speed: number;
  transportMode: EEGTileTransportMode;
  warmVisibleTransfer: boolean;
}): number {
  if (
    params.speed >= 200 &&
    params.transportMode === "play" &&
    !params.warmVisibleTransfer
  ) {
    return 2;
  }

  return 0;
}


/** Minimal request-context shape used by playback/tile helpers. */
export type EEGViewerTileRequestContext = {
  recordingId: string;
  transportMode: EEGTileTransportMode;
  speed: number;
  timeScale: number;
  windowStart: number;
  windowEnd: number;
  actualWindowStart?: number;
  actualWindowEnd?: number;
  requestedLevel: number;
  actualLevel: number;
};

export interface EEGViewerTileContextExpectation {
  recordingId: string;
  transportMode: EEGTileTransportMode;
  speed: number;
  timeScale: number;
  windowStart: number;
}

export function isAbortLikeEegTileRequestError(error: unknown): boolean {
  const candidate = error as {
    code?: string;
    name?: string;
    message?: string;
  };
  const code = String(candidate?.code ?? "");
  const name = String(candidate?.name ?? "");
  const message = String(candidate?.message ?? "").toLowerCase();

  return (
    code === "ERR_CANCELED" ||
    name === "AbortError" ||
    name === "CanceledError" ||
    message.includes("canceled") ||
    message.includes("cancelled") ||
    message.includes("abort")
  );
}

export function shouldRetryEegTileRequest(
  failureCount: number,
  error: unknown,
): boolean {
  const status = (error as any)?.response?.status;
  if (isAbortLikeEegTileRequestError(error)) {
    return false;
  }
  if (status === 409) {
    return false;
  }
  if (status >= 400 && status < 500 && status !== 429) {
    return false;
  }
  return failureCount < 3;
}

export function isEegArtifactRepairConflict(
  error: unknown,
  processingStatus?: string | null,
): boolean {
  const normalizedStatus = (processingStatus || "").trim().toLowerCase();
  if (normalizedStatus === "artifact_missing") {
    return true;
  }
  const message = String(
    (error as any)?.response?.data?.message ||
      (error as any)?.response?.data?.error ||
      (error as Error)?.message ||
      "",
  ).toLowerCase();
  return (
    message.includes("pyramid repair") ||
    message.includes("artifact missing") ||
    message.includes("pyramid artifact missing")
  );
}

/**
 * Drop React Query `eeg-data` entries whose window starts behind the live
 * playhead. Query keys are
 * `["eeg-data", studyId, recordingId, startMs, endMs, ...]`.
 * `keepPreviousData` plus a 30s gcTime previously retained every unique
 * window for the whole soak segment (~150MB → 400MB+ at 8x).
 */
export function shouldDropStaleEegDataQuery(params: {
  queryKey: readonly unknown[];
  recordingId: string;
  currentStartMs: number;
  currentEndMs?: number;
}): boolean {
  if (params.queryKey[0] !== "eeg-data") {
    return false;
  }
  if (params.queryKey[2] !== params.recordingId) {
    return false;
  }
  const startMs = params.queryKey[3];
  const endMs = params.queryKey[4];
  if (typeof startMs !== "number" || !Number.isFinite(startMs)) {
    return false;
  }
  if (startMs < params.currentStartMs) {
    return true;
  }
  return (
    typeof params.currentEndMs === "number" &&
    Number.isFinite(params.currentEndMs) &&
    startMs === params.currentStartMs &&
    typeof endMs === "number" &&
    Number.isFinite(endMs) &&
    endMs !== params.currentEndMs
  );
}

/** Only `uploaded -> reviewed` is in the baseline policy. An explicit
 * transition list is not enough: soak fixtures expose `reviewed` as a
 * target while the server still 400s (already reviewed / policy). */
export function shouldAutoTransitionStudyToReviewed(params: {
  currentStatusCode: string;
}): boolean {
  return params.currentStatusCode.trim().toLowerCase() === "uploaded";
}

export function isEEGViewerTilePayloadStale(
  tileRequestContext: EEGViewerTileRequestContext | null | undefined,
  expected: EEGViewerTileContextExpectation,
): boolean {
  if (!tileRequestContext) {
    return false;
  }

  const staleRecording =
    !!expected.recordingId &&
    tileRequestContext.recordingId !== expected.recordingId;
  const staleTransportMode =
    tileRequestContext.transportMode !== expected.transportMode;
  const staleSpeed =
    Math.abs(tileRequestContext.speed - expected.speed) > 0.001;
  const staleTimeScale =
    Math.abs(tileRequestContext.timeScale - expected.timeScale) > 0.001;
  const staleWindow =
    Math.abs(tileRequestContext.windowStart - expected.windowStart) > 0.001;

  return (
    staleRecording ||
    staleTransportMode ||
    staleSpeed ||
    staleTimeScale ||
    staleWindow
  );
}

export function doesEEGViewerTilePlaybackContextMatch(
  currentContext: EEGViewerTileRequestContext | null | undefined,
  incomingContext: EEGViewerTileRequestContext | null | undefined,
): boolean {
  if (!currentContext || !incomingContext) {
    return false;
  }

  return (
    currentContext.transportMode === incomingContext.transportMode &&
    Math.abs(currentContext.speed - incomingContext.speed) <= 0.001 &&
    Math.abs(currentContext.timeScale - incomingContext.timeScale) <= 0.001 &&
    currentContext.requestedLevel === incomingContext.requestedLevel &&
    currentContext.actualLevel === incomingContext.actualLevel
  );
}

export function resolveEEGViewerOutOfOrderBufferDiscard(params: {
  bufferWindowStart: number;
  loadedWindowStart: number | null | undefined;
  pendingWindowStart: number | null | undefined;
  loadedContext: EEGViewerTileRequestContext | null | undefined;
  pendingContext: EEGViewerTileRequestContext | null | undefined;
  incomingContext: EEGViewerTileRequestContext | null | undefined;
}): {
  staleRelativeToLoaded: boolean;
  staleRelativeToPending: boolean;
} {
  const loadedWindowStart = params.loadedWindowStart ?? -1;
  const pendingWindowStart = params.pendingWindowStart ?? -1;
  const staleRelativeToLoaded =
    params.bufferWindowStart < loadedWindowStart &&
    doesEEGViewerTilePlaybackContextMatch(
      params.loadedContext,
      params.incomingContext,
    );
  const staleRelativeToPending =
    params.bufferWindowStart < pendingWindowStart &&
    doesEEGViewerTilePlaybackContextMatch(
      params.pendingContext,
      params.incomingContext,
    );

  return {
    staleRelativeToLoaded,
    staleRelativeToPending,
  };
}

export function shouldDiscardEEGViewerBackwardPendingBuffer(params: {
  pendingWindowStart: number;
  loadedWindowStart: number | null | undefined;
  loadedContext: EEGViewerTileRequestContext | null | undefined;
  pendingContext: EEGViewerTileRequestContext | null | undefined;
}): boolean {
  const loadedWindowStart = params.loadedWindowStart ?? -1;
  return (
    params.pendingWindowStart < loadedWindowStart &&
    doesEEGViewerTilePlaybackContextMatch(
      params.loadedContext,
      params.pendingContext,
    )
  );
}

export function shouldDiscardEEGViewerFarFuturePendingBuffer(params: {
  pendingWindowStart: number;
  currentPlayhead: number;
  advanceChunk: number;
  loadedContext: EEGViewerTileRequestContext | null | undefined;
  pendingContext: EEGViewerTileRequestContext | null | undefined;
}): boolean {
  if (
    !Number.isFinite(params.pendingWindowStart) ||
    !Number.isFinite(params.currentPlayhead) ||
    !Number.isFinite(params.advanceChunk) ||
    params.advanceChunk <= 0
  ) {
    return false;
  }

  if (
    !doesEEGViewerTilePlaybackContextMatch(
      params.loadedContext,
      params.pendingContext,
    )
  ) {
    return false;
  }

  const maxReasonableLeadSeconds = params.advanceChunk * 2 + 0.25;
  return (
    params.pendingWindowStart >
    Math.max(0, params.currentPlayhead) + maxReasonableLeadSeconds
  );
}

export function shouldRecoverEEGViewerStalledPlaybackWindow(params: {
  speed: number;
  currentPlayhead: number;
  loadedBufferEnd: number | null | undefined;
  timeScale: number;
  hasPendingBuffer: boolean;
  hasScheduledAdvance: boolean;
  lastScheduledAtMs: number | null | undefined;
  nowMs: number;
  /** Optional: allow recovering even with a pending buffer after this hold. */
  pendingStallMaxMs?: number | null;
}): boolean {
  // Timer-scheduler playback (≥8x) can pin forever on a stale scheduled window
  // after a cancelled/dropped handoff. Recovery used to start only at ≥60x,
  // which left 8x–30x soaks frozen at the buffer edge with isPlaying still true.
  if (
    params.speed < 8 ||
    params.loadedBufferEnd === null ||
    params.loadedBufferEnd === undefined
  ) {
    return false;
  }

  const clampBoundary = params.loadedBufferEnd - Math.max(1, params.timeScale);
  if (params.currentPlayhead < clampBoundary - 1.5) {
    return false;
  }

  const lastScheduledAtMs =
    typeof params.lastScheduledAtMs === "number" &&
    Number.isFinite(params.lastScheduledAtMs)
      ? params.lastScheduledAtMs
      : null;
  const pendingStallMaxMs =
    typeof params.pendingStallMaxMs === "number" &&
    Number.isFinite(params.pendingStallMaxMs) &&
    params.pendingStallMaxMs > 0
      ? params.pendingStallMaxMs
      : 3000;

  if (params.hasPendingBuffer) {
    // A decoded pending buffer that never flushes still freezes the clock hold.
    // After pendingStallMaxMs, release the scheduled marker so the tick can
    // leap/flush or re-request instead of waiting forever.
    if (lastScheduledAtMs === null) {
      return true;
    }
    return params.nowMs - lastScheduledAtMs >= pendingStallMaxMs;
  }

  if (!params.hasScheduledAdvance) {
    return false;
  }

  if (lastScheduledAtMs === null) {
    // Speed changes can inherit a scheduled window marker from a lower-speed
    // run, where no high-speed timestamp was recorded. If no pending buffer is
    // present and the clock is already pinned at the buffer edge, release the
    // stale marker immediately so rapid-review playback can request the next
    // tile instead of freezing permanently.
    return true;
  }

  return params.nowMs - lastScheduledAtMs >= 2000;
}

export function shouldHoldEEGViewerPlaybackClockAtRenderableEdge(params: {
  displayTime: number;
  nextTime: number;
  bufferEnd: number;
  timeScale: number;
  currentWindowStart: number;
  loadedWindowStart: number | null | undefined;
  pendingWindowStart: number | null | undefined;
  scheduledWindowStart: number | null | undefined;
  /** Wall-clock time when the playhead first pinned at this edge (optional). */
  holdStartedAtMs?: number | null;
  nowMs?: number;
  /**
   * Escape hatch so a stalled tile handoff cannot freeze soak timing forever.
   * Mid/high-speed paths (≥8x) have data-gap leaps; after this budget the clock
   * may run ahead of the canvas briefly until the next buffer flushes.
   */
  maxHoldMs?: number | null;
}): boolean {
  const values = [
    params.displayTime,
    params.nextTime,
    params.bufferEnd,
    params.timeScale,
    params.currentWindowStart,
  ];
  if (values.some((value) => !Number.isFinite(value))) {
    return false;
  }

  const timeScale = Math.max(1, params.timeScale);
  const clampGap = params.nextTime - params.displayTime;
  if (clampGap <= 0.05 || params.bufferEnd <= timeScale) {
    return false;
  }

  const clampBoundary = params.bufferEnd - timeScale;
  if (clampBoundary < 0 || Math.abs(params.displayTime - clampBoundary) > 0.05) {
    return false;
  }

  const loadedWindowStart =
    typeof params.loadedWindowStart === "number" &&
    Number.isFinite(params.loadedWindowStart)
      ? params.loadedWindowStart
      : params.currentWindowStart;
  const pendingWindowStart =
    typeof params.pendingWindowStart === "number" &&
    Number.isFinite(params.pendingWindowStart)
      ? params.pendingWindowStart
      : null;
  const scheduledWindowStart =
    typeof params.scheduledWindowStart === "number" &&
    Number.isFinite(params.scheduledWindowStart)
      ? params.scheduledWindowStart
      : null;
  const nextWindowThreshold = loadedWindowStart + 0.25;

  const hasFutureHandoff =
    (pendingWindowStart !== null && pendingWindowStart > nextWindowThreshold) ||
    (scheduledWindowStart !== null && scheduledWindowStart > nextWindowThreshold);
  if (!hasFutureHandoff) {
    return false;
  }

  const maxHoldMs =
    typeof params.maxHoldMs === "number" &&
    Number.isFinite(params.maxHoldMs) &&
    params.maxHoldMs > 0
      ? params.maxHoldMs
      : null;
  const holdStartedAtMs =
    typeof params.holdStartedAtMs === "number" &&
    Number.isFinite(params.holdStartedAtMs)
      ? params.holdStartedAtMs
      : null;
  const nowMs =
    typeof params.nowMs === "number" && Number.isFinite(params.nowMs)
      ? params.nowMs
      : null;
  if (
    maxHoldMs !== null &&
    holdStartedAtMs !== null &&
    nowMs !== null &&
    nowMs - holdStartedAtMs >= maxHoldMs
  ) {
    return false;
  }

  return true;
}

/**
 * Mid-speed data-gap leaps skip unfetched EEG time instantly, which inflates
 * observed soak speed. Require the leap distance to be "earned" in wall time
 * (leapSeconds/speed) before leaping; ultra-rapid (≥100x) still leaps
 * immediately to avoid freezes.
 */
export function resolveEEGViewerDataGapLeapDecision(params: {
  speed: number;
  nowMs: number;
  pendingWindowStart: number;
  /** Current playhead (typically at clamp) — earns the full leap distance. */
  playheadSeconds: number;
  gapHoldStartedAtMs: number | null;
}): { shouldLeap: boolean; nextGapHoldStartedAtMs: number | null } {
  const speed =
    typeof params.speed === "number" && Number.isFinite(params.speed)
      ? Math.abs(params.speed)
      : 1;
  const leapSeconds = params.pendingWindowStart - params.playheadSeconds;
  if (!Number.isFinite(leapSeconds) || leapSeconds <= 0.05) {
    return { shouldLeap: true, nextGapHoldStartedAtMs: null };
  }
  if (speed >= 100) {
    return { shouldLeap: true, nextGapHoldStartedAtMs: null };
  }

  const earnMs = (leapSeconds / Math.max(1, speed)) * 1000;
  const startedAtMs =
    typeof params.gapHoldStartedAtMs === "number" &&
    Number.isFinite(params.gapHoldStartedAtMs)
      ? params.gapHoldStartedAtMs
      : params.nowMs;
  if (params.nowMs - startedAtMs >= earnMs) {
    return { shouldLeap: true, nextGapHoldStartedAtMs: null };
  }
  return { shouldLeap: false, nextGapHoldStartedAtMs: startedAtMs };
}

export function resolveEEGViewerMonotonicPlaybackDisplayTime(params: {
  previousTime: number | null | undefined;
  proposedTime: number;
  rawNextTime: number;
  timeScale: number;
}): number {
  if (!Number.isFinite(params.proposedTime)) {
    return 0;
  }
  const previousTime =
    typeof params.previousTime === "number" && Number.isFinite(params.previousTime)
      ? params.previousTime
      : null;
  if (previousTime === null) {
    return Math.max(0, params.proposedTime);
  }

  const timeScale = Math.max(1, params.timeScale || 1);
  const explicitBackwardSeek =
    Number.isFinite(params.rawNextTime) &&
    params.rawNextTime < previousTime - Math.max(1, timeScale * 0.5);
  if (explicitBackwardSeek) {
    return Math.max(0, params.proposedTime);
  }

  return Math.max(previousTime, params.proposedTime);
}

export function shouldDiscardEEGViewerPausedPlayHoldBuffer(params: {
  isPlaying: boolean;
  pausedHoldTransportMode: EEGTileTransportMode | null | undefined;
  bufferWindowStart: number;
  loadedWindowStart: number | null | undefined;
  incomingContext: EEGViewerTileRequestContext | null | undefined;
}): boolean {
  if (params.isPlaying) {
    return false;
  }

  if (params.pausedHoldTransportMode !== "play") {
    return false;
  }

  if (
    !params.incomingContext ||
    params.incomingContext.transportMode !== "play"
  ) {
    return false;
  }

  return true;
}

export function shouldDiscardEEGViewerFuturePendingBufferOnPause(params: {
  pendingWindowStart: number | null | undefined;
  loadedWindowStart: number | null | undefined;
}): boolean {
  const pendingWindowStart =
    typeof params.pendingWindowStart === "number" &&
    Number.isFinite(params.pendingWindowStart)
      ? params.pendingWindowStart
      : null;
  const loadedWindowStart =
    typeof params.loadedWindowStart === "number" &&
    Number.isFinite(params.loadedWindowStart)
      ? params.loadedWindowStart
      : null;

  return (
    pendingWindowStart !== null &&
    loadedWindowStart !== null &&
    pendingWindowStart > loadedWindowStart + 0.25
  );
}

export function shouldResetEEGViewerPausedPlayQueryWindow(params: {
  loadedWindowStart: number | null | undefined;
  scheduledWindowStart: number | null | undefined;
  dataWindowStart: number | null | undefined;
  pendingWindowStart: number | null | undefined;
}): boolean {
  const loadedWindowStart =
    typeof params.loadedWindowStart === "number" &&
    Number.isFinite(params.loadedWindowStart)
      ? params.loadedWindowStart
      : null;

  if (loadedWindowStart === null) {
    return false;
  }

  const candidates = [
    params.scheduledWindowStart,
    params.dataWindowStart,
    params.pendingWindowStart,
  ];

  return candidates.some(
    (candidate) =>
      typeof candidate === "number" &&
      Number.isFinite(candidate) &&
      candidate > loadedWindowStart + 0.25,
  );
}

export function resolveEEGViewerMaxPlaybackTime(
  recordingDuration: number,
  _timeScale: number,
): number {
  const safeRecordingDuration = Number.isFinite(recordingDuration)
    ? Math.max(0, recordingDuration)
    : 0;
  return safeRecordingDuration;
}

export function resolveEEGViewerAdvanceBoundaryWindowStart(
  actualDataEnd: number,
  advanceChunk: number,
): number {
  const safeActualDataEnd = Number.isFinite(actualDataEnd)
    ? Math.max(0, actualDataEnd)
    : 0;
  const safeAdvanceChunk = Number.isFinite(advanceChunk)
    ? Math.max(1, advanceChunk)
    : 1;
  const boundaryEpsilon = safeAdvanceChunk * 1e-6;
  const boundaryStep = Math.ceil(
    (safeActualDataEnd - boundaryEpsilon) / safeAdvanceChunk,
  );

  return Math.max(0, boundaryStep * safeAdvanceChunk);
}

export function shouldResetEEGViewerWindowStartOnSeek(params: {
  targetTime: number;
  dataWindowStart: number;
  bufferSize: number;
  timeScale: number;
  loadedWindowStart?: number | null | undefined;
  loadedBufferEnd?: number | null | undefined;
}): boolean {
  const safeTargetTime = Number.isFinite(params.targetTime)
    ? Math.max(0, params.targetTime)
    : 0;
  const safeDataWindowStart = Number.isFinite(params.dataWindowStart)
    ? Math.max(0, params.dataWindowStart)
    : 0;
  const safeBufferSize = Number.isFinite(params.bufferSize)
    ? Math.max(1, params.bufferSize)
    : 1;
  const safeTimeScale = Number.isFinite(params.timeScale)
    ? Math.max(1, params.timeScale)
    : 1;
  const computedWindowEnd =
    safeDataWindowStart + safeBufferSize - safeTimeScale;
  const outsideComputedWindow =
    safeTargetTime < safeDataWindowStart ||
    safeTargetTime > computedWindowEnd + 0.25;

  const loadedWindowStart =
    typeof params.loadedWindowStart === "number" &&
    Number.isFinite(params.loadedWindowStart)
      ? Math.max(0, params.loadedWindowStart)
      : null;
  if (loadedWindowStart === null) {
    return true;
  }

  if (safeTargetTime + 0.25 < loadedWindowStart) {
    return true;
  }

  const loadedBufferEnd =
    typeof params.loadedBufferEnd === "number" &&
    Number.isFinite(params.loadedBufferEnd)
      ? Math.max(0, params.loadedBufferEnd)
      : null;
  if (loadedBufferEnd !== null) {
    const latestVisibleStart = Math.max(
      loadedWindowStart,
      loadedBufferEnd - safeTimeScale,
    );
    if (safeTargetTime > latestVisibleStart + 0.25) {
      return true;
    }
  }

  return outsideComputedWindow;
}

export function shouldDeferEEGViewerPausedSeekUntilBufferLoad(params: {
  isPlaying: boolean;
  resetsWindowStart: boolean;
  targetTime: number;
  pendingWindowStart?: number | null | undefined;
  pendingBufferEnd?: number | null | undefined;
  timeScale?: number | null | undefined;
  reason?: { source?: string };
}): boolean {
  // User-initiated timeline scrub and go-to-time should always seek immediately —
  // the prefetch triggered by resetsWindowStart will load the required tile in
  // the background. Deferring these seeks creates a negative UX loop: every
  // jump at a different position discards the previous pending buffer
  // (pendingConflictsWithTarget), so the deferred seek is never applied and the
  // user sees zero visual feedback.
  if (
    params.reason?.source === "annotation-timeline" ||
    params.reason?.source === "go-to-time-input" ||
    params.reason?.source === "page-nav" ||
    params.reason?.source === "fine-pan" ||
    params.reason?.source === "go-to-start"
  ) {
    return false;
  }

  if (params.isPlaying || !params.resetsWindowStart) {
    return false;
  }

  const safeTargetTime = Number.isFinite(params.targetTime)
    ? Math.max(0, params.targetTime)
    : 0;
  const pendingWindowStart =
    typeof params.pendingWindowStart === "number" &&
    Number.isFinite(params.pendingWindowStart)
      ? Math.max(0, params.pendingWindowStart)
      : null;
  const pendingBufferEnd =
    typeof params.pendingBufferEnd === "number" &&
    Number.isFinite(params.pendingBufferEnd)
      ? Math.max(0, params.pendingBufferEnd)
      : null;
  const timeScale =
    typeof params.timeScale === "number" && Number.isFinite(params.timeScale)
      ? Math.max(1, params.timeScale)
      : 1;

  if (
    pendingWindowStart === null ||
    pendingBufferEnd === null ||
    pendingBufferEnd <= pendingWindowStart
  ) {
    return true;
  }

  const latestVisibleStart = Math.max(
    pendingWindowStart,
    pendingBufferEnd - timeScale,
  );

  return (
    safeTargetTime + 0.25 < pendingWindowStart ||
    safeTargetTime > latestVisibleStart + 0.25
  );
}

export function shouldExitEEGViewerPausedPlayHoldOnManualSeek(params: {
  isPlaying: boolean;
  pausedHoldTransportMode?: EEGTileTransportMode | null | undefined;
}): boolean {
  return !params.isPlaying && params.pausedHoldTransportMode === "play";
}

export function resolveEEGViewerRequestedTime(params: {
  currentTime: number;
  pendingSeekTime?: number | null | undefined;
}): number {
  const pendingSeekTime =
    typeof params.pendingSeekTime === "number" &&
    Number.isFinite(params.pendingSeekTime)
      ? Math.max(0, params.pendingSeekTime)
      : null;

  if (pendingSeekTime !== null) {
    return pendingSeekTime;
  }

  return Number.isFinite(params.currentTime)
    ? Math.max(0, params.currentTime)
    : 0;
}

type EEGViewerTileTimingLike = {
  numSamples: number;
  sampling_rate: number;
  duration: number;
  actualDataDuration?: number;
  envelopeMin?: Float32Array[];
  envelopeMax?: Float32Array[];
  requestContext?: EEGViewerTileRequestContext | null;
};

function hasEEGViewerEnvelopeColumns(tile: EEGViewerTileTimingLike): boolean {
  return Boolean(
    (tile.envelopeMin && tile.envelopeMin.length > 0) ||
    (tile.envelopeMax && tile.envelopeMax.length > 0),
  );
}

function resolveEEGViewerRequestedWindowDuration(
  requestContext: EEGViewerTileRequestContext | null | undefined,
): number | null {
  if (!requestContext) {
    return null;
  }

  const requestedDuration =
    requestContext.windowEnd - requestContext.windowStart;

  return Number.isFinite(requestedDuration) && requestedDuration > 0
    ? requestedDuration
    : null;
}

function resolveEEGViewerActualWindowDuration(
  requestContext: EEGViewerTileRequestContext | null | undefined,
): number | null {
  if (!requestContext) {
    return null;
  }

  const actualWindowStart =
    typeof requestContext.actualWindowStart === "number" &&
    Number.isFinite(requestContext.actualWindowStart)
      ? requestContext.actualWindowStart
      : null;
  const actualWindowEnd =
    typeof requestContext.actualWindowEnd === "number" &&
    Number.isFinite(requestContext.actualWindowEnd)
      ? requestContext.actualWindowEnd
      : null;

  if (actualWindowStart === null || actualWindowEnd === null) {
    return null;
  }

  const actualDuration = actualWindowEnd - actualWindowStart;
  return Number.isFinite(actualDuration) && actualDuration > 0
    ? actualDuration
    : null;
}

export function resolveEEGViewerTileLogicalDuration(
  tile: EEGViewerTileTimingLike,
): number {
  const requestedDuration = resolveEEGViewerRequestedWindowDuration(
    tile.requestContext,
  );

  if (hasEEGViewerEnvelopeColumns(tile) && requestedDuration !== null) {
    return requestedDuration;
  }

  if (
    typeof tile.actualDataDuration === "number" &&
    Number.isFinite(tile.actualDataDuration) &&
    tile.actualDataDuration > 0
  ) {
    return tile.actualDataDuration;
  }

  const actualWindowDuration = resolveEEGViewerActualWindowDuration(
    tile.requestContext,
  );
  if (actualWindowDuration !== null) {
    return actualWindowDuration;
  }

  if (
    typeof tile.duration === "number" &&
    Number.isFinite(tile.duration) &&
    tile.duration > 0
  ) {
    return tile.duration;
  }

  if (
    typeof tile.sampling_rate === "number" &&
    Number.isFinite(tile.sampling_rate) &&
    tile.sampling_rate > 0 &&
    Number.isFinite(tile.numSamples) &&
    tile.numSamples > 0
  ) {
    return tile.numSamples / tile.sampling_rate;
  }

  return requestedDuration ?? 0;
}

export function resolveEEGViewerTileSampleRate(
  tile: EEGViewerTileTimingLike,
): number {
  if (hasEEGViewerEnvelopeColumns(tile)) {
    const logicalDuration = resolveEEGViewerTileLogicalDuration(tile);
    if (
      logicalDuration > 0 &&
      Number.isFinite(tile.numSamples) &&
      tile.numSamples > 0
    ) {
      return tile.numSamples / logicalDuration;
    }
  }

  if (
    typeof tile.sampling_rate === "number" &&
    Number.isFinite(tile.sampling_rate) &&
    tile.sampling_rate > 0
  ) {
    return tile.sampling_rate;
  }

  const logicalDuration = resolveEEGViewerTileLogicalDuration(tile);
  if (
    logicalDuration > 0 &&
    Number.isFinite(tile.numSamples) &&
    tile.numSamples > 0
  ) {
    return tile.numSamples / logicalDuration;
  }

  return 0;
}

export function resolveEEGViewerHighSpeedPlaySeedWindowStarts(params: {
  isPlaying: boolean;
  playbackSpeed: number;
  dataWindowStart: number;
  advanceChunkSec: number;
  currentPlayhead?: number | null | undefined;
  bufferSizeSec?: number | null | undefined;
  recordingDurationSec?: number | null | undefined;
}): number[] {
  if (params.isPlaying || params.playbackSpeed < 100) {
    return [];
  }

  const advanceChunkSec =
    Number.isFinite(params.advanceChunkSec) && params.advanceChunkSec > 0
      ? params.advanceChunkSec
      : null;
  if (advanceChunkSec === null) {
    return [];
  }

  let baseWindowStart =
    Number.isFinite(params.dataWindowStart) && params.dataWindowStart > 0
      ? params.dataWindowStart
      : null;

  if (baseWindowStart === null) {
    const currentPlayhead =
      typeof params.currentPlayhead === "number" &&
      Number.isFinite(params.currentPlayhead)
        ? Math.max(0, params.currentPlayhead)
        : null;
    const bufferSizeSec =
      typeof params.bufferSizeSec === "number" &&
      Number.isFinite(params.bufferSizeSec) &&
      params.bufferSizeSec > 0
        ? params.bufferSizeSec
        : null;

    if (currentPlayhead === null || bufferSizeSec === null) {
      return [];
    }

    baseWindowStart = alignTileWindowToBackend({
      windowStartSec: currentPlayhead,
      bufferSizeSec,
      speed: params.playbackSpeed,
      transportMode: "play",
      recordingDurationSec:
        typeof params.recordingDurationSec === "number" &&
        Number.isFinite(params.recordingDurationSec) &&
        params.recordingDurationSec > 0
          ? params.recordingDurationSec
          : undefined,
    }).startSec;
  }

  return [baseWindowStart, baseWindowStart + advanceChunkSec];
}

export function resolveEEGViewerPausedHighSpeedPlaySeedWindowCount(
  playbackSpeed: number,
): number {
  if (!Number.isFinite(playbackSpeed) || playbackSpeed < 100) {
    return 0;
  }

  if (playbackSpeed >= 150 && playbackSpeed < 200) {
    return 1;
  }

  return 2;
}

export function shouldLoadEEGViewerPlaybackBootstrapBuffer(params: {
  isPlaying: boolean;
  currentPlayhead: number;
  bufferWindowStart: number;
  bufferDuration: number;
  loadedWindowStart: number | null | undefined;
  pendingWindowStart: number | null | undefined;
  loadedContext: EEGViewerTileRequestContext | null | undefined;
  incomingContext: EEGViewerTileRequestContext | null | undefined;
}): boolean {
  if (params.isPlaying !== true) {
    return false;
  }

  if (!params.loadedContext || !params.incomingContext) {
    return false;
  }

  if (
    params.loadedContext.recordingId !== params.incomingContext.recordingId ||
    params.loadedContext.transportMode !== "play" ||
    params.incomingContext.transportMode !== "play"
  ) {
    return false;
  }

  const loadedWindowStart =
    typeof params.loadedWindowStart === "number" &&
    Number.isFinite(params.loadedWindowStart)
      ? params.loadedWindowStart
      : null;
  if (loadedWindowStart === null) {
    return false;
  }

  const currentPlayhead =
    Number.isFinite(params.currentPlayhead) && params.currentPlayhead >= 0
      ? params.currentPlayhead
      : null;
  const bufferDuration =
    Number.isFinite(params.bufferDuration) && params.bufferDuration > 0
      ? params.bufferDuration
      : null;
  if (currentPlayhead === null || bufferDuration === null) {
    return false;
  }

  const bufferEnd = params.bufferWindowStart + bufferDuration;
  const coversCurrentPlayhead =
    currentPlayhead + 0.25 >= params.bufferWindowStart &&
    currentPlayhead <= bufferEnd + 0.25;
  const arrivesEarlierThanLoaded =
    params.bufferWindowStart + 0.25 < loadedWindowStart;
  const playheadIsBehindLoadedWindow =
    currentPlayhead <= loadedWindowStart + 0.25;
  const pendingWindowStart =
    typeof params.pendingWindowStart === "number" &&
    Number.isFinite(params.pendingWindowStart)
      ? params.pendingWindowStart
      : null;
  const supersededByPendingWindow =
    pendingWindowStart !== null &&
    pendingWindowStart <= params.bufferWindowStart + 0.25;

  return (
    coversCurrentPlayhead &&
    arrivesEarlierThanLoaded &&
    playheadIsBehindLoadedWindow &&
    !supersededByPendingWindow
  );
}

export function resolveEEGViewerPausedFrameTime(params: {
  frozenPlayheadTime: number;
  rendererPausedTime: number | null | undefined;
  loadedWindowStart?: number | null | undefined;
  loadedBufferEnd?: number | null | undefined;
  timeScale?: number | null | undefined;
}): number {
  const safeFrozenPlayheadTime = Number.isFinite(params.frozenPlayheadTime)
    ? Math.max(0, params.frozenPlayheadTime)
    : 0;
  const safeRendererPausedTime =
    typeof params.rendererPausedTime === "number" &&
    Number.isFinite(params.rendererPausedTime)
      ? Math.max(0, params.rendererPausedTime)
      : null;
  const loadedWindowStart =
    typeof params.loadedWindowStart === "number" &&
    Number.isFinite(params.loadedWindowStart)
      ? Math.max(0, params.loadedWindowStart)
      : null;
  const loadedBufferEnd =
    typeof params.loadedBufferEnd === "number" &&
    Number.isFinite(params.loadedBufferEnd)
      ? Math.max(0, params.loadedBufferEnd)
      : null;
  const timeScale =
    typeof params.timeScale === "number" && Number.isFinite(params.timeScale)
      ? Math.max(1, params.timeScale)
      : null;

  let pausedTime = safeRendererPausedTime ?? safeFrozenPlayheadTime;

  if (loadedWindowStart !== null && pausedTime < loadedWindowStart) {
    pausedTime = loadedWindowStart;
  }

  if (loadedBufferEnd !== null && timeScale !== null) {
    const latestVisibleStart = Math.max(0, loadedBufferEnd - timeScale);
    if (loadedWindowStart !== null && latestVisibleStart < loadedWindowStart) {
      pausedTime = Math.max(pausedTime, loadedWindowStart);
    } else {
      pausedTime = Math.min(pausedTime, latestVisibleStart);
      if (loadedWindowStart !== null) {
        pausedTime = Math.max(pausedTime, loadedWindowStart);
      }
    }
  }

  return pausedTime;
}

export function resolveEEGViewerLastPaintedPlaybackTime(params: {
  previousPaintedTime: number | null | undefined;
  displayTime: number;
  paintedFrame: boolean;
}): number | null {
  if (!params.paintedFrame) {
    return typeof params.previousPaintedTime === "number"
      ? Math.max(0, params.previousPaintedTime)
      : null;
  }

  return Number.isFinite(params.displayTime)
    ? Math.max(0, params.displayTime)
    : typeof params.previousPaintedTime === "number"
      ? Math.max(0, params.previousPaintedTime)
      : null;
}

/** Play-mode speeds that blit any forward overlap (deltaPx < canvas width). */
export const EEG_VIEWER_TRUE_OVERLAP_BLIT_MIN_SPEED = 16;

export function resolveEEGViewerPlaybackMaxBlitFraction(
  playbackSpeed: number,
): number {
  return Number.isFinite(playbackSpeed) &&
    playbackSpeed >= EEG_VIEWER_TRUE_OVERLAP_BLIT_MIN_SPEED
    ? 1
    : 0.25;
}

export function resolveEEGViewerPlaybackBlitSafeSeconds(
  playbackSpeed: number,
  timeScaleSeconds = 10,
): number {
  const ceilingMs = resolveEEGViewerPlaybackBlitSafeCeilingMs(
    playbackSpeed,
    timeScaleSeconds,
  );
  if (!Number.isFinite(ceilingMs)) {
    return Number.POSITIVE_INFINITY;
  }
  return (ceilingMs / 1000) * Math.max(1, Math.abs(playbackSpeed));
}

export function resolveEEGViewerPlaybackBlitStepSeconds(
  playbackSpeed: number,
  timeScaleSeconds = 10,
): number {
  const blitSafeSeconds = resolveEEGViewerPlaybackBlitSafeSeconds(
    playbackSpeed,
    timeScaleSeconds,
  );
  const pageSeconds =
    Number.isFinite(timeScaleSeconds) && timeScaleSeconds > 0
      ? timeScaleSeconds
      : 10;
  // Ultra-rapid ticks have almost no missed-frame margin; keep each step
  // well under one scrollable page so a hitch still blits instead of redrawing.
  const pageStepSeconds =
    playbackSpeed >= 100 ? pageSeconds * 0.4 : pageSeconds * 0.7;
  return Math.min(blitSafeSeconds * 0.7, pageStepSeconds);
}

export function shouldSplitEEGViewerWaveformPaintForBlit(params: {
  gapSeconds: number;
  playbackSpeed: number;
  timeScaleSeconds: number;
}): boolean {
  const blitSafeSeconds = resolveEEGViewerPlaybackBlitSafeSeconds(
    params.playbackSpeed,
    params.timeScaleSeconds,
  );
  const pageSeconds =
    Number.isFinite(params.timeScaleSeconds) && params.timeScaleSeconds > 0
      ? params.timeScaleSeconds
      : 10;
  const splitAfterSeconds = Math.min(
    blitSafeSeconds * 0.9,
    pageSeconds * (params.playbackSpeed >= 100 ? 0.45 : 0.7),
  );
  return (
    Number.isFinite(splitAfterSeconds) &&
    splitAfterSeconds > 0 &&
    params.gapSeconds > splitAfterSeconds
  );
}

export function resolveEEGViewerPlaybackRenderIntervalMs(
  playbackSpeed: number,
): number {
  if (!Number.isFinite(playbackSpeed) || playbackSpeed < 30) {
    return 0;
  }

  // Intervals must stay below ~(timeScale/speed)*1000 so high-speed paints
  // keep deltaPx < canvas width and retain scrolling blit reuse.
  // 30x–59x: 60x already passes the 250ms long-task budget at 80ms. Painting
  // 30x at 33 Hz stacked loadChannels + full redraws into 350–390ms LoAFs.
  if (playbackSpeed < 100) {
    return 80;
  }

  if (playbackSpeed < 150) {
    return 40;
  }

  if (playbackSpeed < 200) {
    return 24;
  }

  return 20;
}

/** Max wall interval before adjacent paints exceed one scrollable page (blit). */
export function resolveEEGViewerPlaybackBlitSafeCeilingMs(
  playbackSpeed: number,
  timeScaleSeconds = 10,
): number {
  if (!Number.isFinite(playbackSpeed) || playbackSpeed < 1) {
    return Number.POSITIVE_INFINITY;
  }
  const timeScale =
    Number.isFinite(timeScaleSeconds) && timeScaleSeconds > 0
      ? timeScaleSeconds
      : 10;
  return Math.max(
    16,
    (timeScale / Math.max(1, playbackSpeed)) * 1000 * 0.75,
  );
}

/**
 * Timer-scheduler cadence for usePlaybackClock. Must stay ≤ blit-safe ceiling
 * (and prefer the paint interval when set) so each tick's EEG Δt still fits a
 * scrolling blit instead of forcing a full redraw.
 */
export function resolveEEGViewerPlaybackSchedulerIntervalMs(
  playbackSpeed: number,
  timeScaleSeconds = 10,
): number {
  if (!Number.isFinite(playbackSpeed) || playbackSpeed < 8) {
    return 0;
  }

  const blitSafeCeilingMs = resolveEEGViewerPlaybackBlitSafeCeilingMs(
    playbackSpeed,
    timeScaleSeconds,
  );
  const paintIntervalMs =
    resolveEEGViewerPlaybackRenderIntervalMs(playbackSpeed);

  if (paintIntervalMs > 0) {
    return Math.min(paintIntervalMs, blitSafeCeilingMs);
  }

  // Mid-speed (8x–29x): ~30 Hz keeps timing smooth without over-driving paint.
  return Math.min(33, blitSafeCeilingMs);
}

export function resolveEEGViewerAdaptivePlaybackRenderIntervalMs(
  playbackSpeed: number,
  previousRenderDurationMs: number,
  timeScaleSeconds = 10,
): number {
  const baseInterval = resolveEEGViewerPlaybackRenderIntervalMs(playbackSpeed);
  if (baseInterval <= 0 || !Number.isFinite(previousRenderDurationMs)) {
    return baseInterval;
  }

  const blitSafeCeilingMs = resolveEEGViewerPlaybackBlitSafeCeilingMs(
    playbackSpeed,
    timeScaleSeconds,
  );

  // Ultra-rapid: stretch toward the blit-safe ceiling when a paint is
  // expensive, but never past it. A fixed 35–50ms cadence after a 300ms
  // redraw queues a full-page catch-up and wedges Playwright evaluates.
  if (playbackSpeed >= 100) {
    const recoveredIntervalMs = Math.max(
      baseInterval,
      Math.min(blitSafeCeilingMs, previousRenderDurationMs * 2),
    );
    return Math.min(recoveredIntervalMs, blitSafeCeilingMs);
  }

  // Keep synchronous waveform painting below roughly one quarter of available
  // main-thread time during rapid review. This preserves clock/input progress
  // when a pathological paint takes hundreds of milliseconds.
  const adaptiveIntervalMs = Math.max(
    baseInterval,
    Math.min(1000, previousRenderDurationMs * 4),
  );

  // Hard ceiling so adjacent painted frames still overlap (blit-capable).
  // Using Math.min (not Math.max) — the previous formula inverted the cap.
  if (
    Number.isFinite(timeScaleSeconds) &&
    timeScaleSeconds > 0 &&
    playbackSpeed >= 30
  ) {
    return Math.min(adaptiveIntervalMs, blitSafeCeilingMs);
  }

  return adaptiveIntervalMs;
}

export function shouldSuspendEEGViewerLiveTopographySampling(params: {
  isPlaying: boolean;
  playbackSpeed: number;
  dockCollapsed: boolean;
}): boolean {
  if (params.dockCollapsed) {
    return true;
  }

  return params.isPlaying && params.playbackSpeed >= 150;
}

export function shouldSuspendEEGViewerAnnotationTimeline(params: {
  activeHighSpeedPlayback: boolean;
  showAnnotationPanel: boolean;
}): boolean {
  return params.activeHighSpeedPlayback && !params.showAnnotationPanel;
}

export function shouldSuspendEEGViewerClinicalDockRendering(params: {
  isPlaying: boolean;
  playbackSpeed: number;
  dockCollapsed: boolean;
}): boolean {
  if (params.dockCollapsed) {
    return false;
  }

  return params.isPlaying && params.playbackSpeed >= 150;
}

export function shouldFetchEEGViewerAnnotations(params: {
  studyId: string | null | undefined;
  activeHighSpeedPlayback: boolean;
  showAnnotationPanel: boolean;
  annotationTimelineVisible: boolean;
  /** Sprint 4B: keep annotation fetches off the first-playable path. */
  viewerChromeReady?: boolean;
}): boolean {
  if (!params.studyId) {
    return false;
  }

  if (params.viewerChromeReady === false) {
    return false;
  }

  return (
    !params.activeHighSpeedPlayback ||
    params.showAnnotationPanel ||
    params.annotationTimelineVisible
  );
}

export function shouldComputeEEGViewerTrendInsights(params: {
  viewerChromeReady: boolean;
  showAnnotationPanel: boolean;
  trendCorrelationVisible: boolean;
  trendEvalActive: boolean;
  hasSelectedIntervention: boolean;
}): boolean {
  if (!params.viewerChromeReady) {
    return false;
  }

  return (
    params.showAnnotationPanel ||
    params.trendCorrelationVisible ||
    params.trendEvalActive ||
    params.hasSelectedIntervention
  );
}

/** Native HTMLVideoElement.playbackRate cannot represent rapid-review EEG. */
export const EEG_VIDEO_NATIVE_RATE_MAX = 16;

/**
 * Video is a slave clock. Do not copy its reported rate onto EEG when EEG is
 * already in rapid review — native video clamps to ≤16x and falls back to 1x,
 * which collapses grid/sensitivity/time-scale/blit and looks like a fade.
 */
export function shouldApplyVideoSlaveSpeedToEeg(params: {
  eegSpeed: number;
  videoReportedSpeed: number;
}): boolean {
  const eeg = Math.abs(Number(params.eegSpeed) || 1);
  const video = Math.abs(Number(params.videoReportedSpeed));
  if (!Number.isFinite(video) || video <= 0) return false;
  if (Math.abs(eeg - video) <= 0.001) return false;
  if (eeg > EEG_VIDEO_NATIVE_RATE_MAX) return false;
  return true;
}

/**
 * Video is a slave clock. Do not pull the EEG playhead to a lagging, looping,
 * or ended <video> while EEG is already playing faster than careful review.
 * That rewind collapses rapid-review paint and soak timing to 1x.
 */
export function shouldApplyVideoSlaveSeekToEeg(params: {
  eegSpeed: number;
  eegIsPlaying: boolean;
}): boolean {
  if (!params.eegIsPlaying) return true;
  return Math.abs(Number(params.eegSpeed) || 1) <= 2;
}

export function resolveEEGViewerPlaybackPrefetchPlan(params: {
  speed: number;
  transportMode: EEGTileTransportMode;
  warmVisibleTransfer: boolean;
  sincePlaybackStartMs: number | null | undefined;
}): {
  totalWindowCount: number;
  primeWindowCount: number;
  cadenceWindows: number;
} {
  if (params.transportMode !== "play" || params.speed < 16) {
    return {
      totalWindowCount: 0,
      primeWindowCount: 0,
      cadenceWindows: 1,
    };
  }

  if (params.speed >= 200) {
    const settledRapidReview =
      typeof params.sincePlaybackStartMs === "number" &&
      Number.isFinite(params.sincePlaybackStartMs) &&
      params.sincePlaybackStartMs >= 5000;

    if (!params.warmVisibleTransfer) {
      return {
        totalWindowCount: 2,
        primeWindowCount: 0,
        cadenceWindows: 1,
      };
    }

    return {
      totalWindowCount: settledRapidReview ? 4 : 3,
      primeWindowCount: settledRapidReview ? 1 : 0,
      cadenceWindows: settledRapidReview ? 3 : 2,
    };
  }

  if (params.speed >= 100) {
    return {
      totalWindowCount: params.warmVisibleTransfer ? 2 : 1,
      primeWindowCount: params.warmVisibleTransfer ? 1 : 0,
      cadenceWindows: 1,
    };
  }

  return {
    totalWindowCount: params.speed >= 50 ? 3 : 5,
    primeWindowCount: 0,
    cadenceWindows: 1,
  };
}

export function shouldScheduleEEGViewerPlaybackBackgroundPrefetch(params: {
  speed: number;
  transportMode: EEGTileTransportMode;
  currentWindowStartSec: number;
  advanceChunkSec: number;
  cadenceWindows: number;
  lastScheduledWindowStartSec: number | null | undefined;
}): boolean {
  if (params.transportMode !== "play" || params.speed < 200) {
    return true;
  }

  const lastScheduledWindowStartSec =
    typeof params.lastScheduledWindowStartSec === "number" &&
    Number.isFinite(params.lastScheduledWindowStartSec)
      ? params.lastScheduledWindowStartSec
      : null;
  if (lastScheduledWindowStartSec === null) {
    return true;
  }

  const cadenceWindows = Math.max(1, Math.trunc(params.cadenceWindows));
  const cadenceBoundarySec =
    lastScheduledWindowStartSec + params.advanceChunkSec * cadenceWindows;
  return (
    params.currentWindowStartSec + Math.abs(params.advanceChunkSec) * 1e-6 >=
    cadenceBoundarySec
  );
}

export function shouldScheduleEEGViewerPlaybackWindowTransition(params: {
  speed: number;
  nextWindowStart: number;
  scheduledWindowStart: number;
  lastScheduledAtMs: number | null | undefined;
  nowMs: number;
}): boolean {
  if (!(params.nextWindowStart > params.scheduledWindowStart)) {
    return false;
  }

  if (params.speed < 150) {
    return true;
  }

  const lastScheduledAtMs =
    typeof params.lastScheduledAtMs === "number" &&
    Number.isFinite(params.lastScheduledAtMs)
      ? params.lastScheduledAtMs
      : null;
  if (lastScheduledAtMs === null) {
    return true;
  }

  return params.nowMs - lastScheduledAtMs >= 180;
}

function lowerBoundSortedNumbers(
  values: readonly number[],
  target: number,
): number {
  let low = 0;
  let high = values.length;

  while (low < high) {
    const middle = low + Math.floor((high - low) / 2);
    if (values[middle] < target) {
      low = middle + 1;
    } else {
      high = middle;
    }
  }

  return low;
}

function upperBoundSortedNumbers(
  values: readonly number[],
  target: number,
): number {
  let low = 0;
  let high = values.length;

  while (low < high) {
    const middle = low + Math.floor((high - low) / 2);
    if (values[middle] <= target) {
      low = middle + 1;
    } else {
      high = middle;
    }
  }

  return low;
}

export function resolveEEGViewerEventNavigationState(params: {
  eventIndex: readonly number[];
  currentTime: number;
  epsilon?: number;
}): {
  previousEventCount: number;
  nextEventCount: number;
  previousEventTime: number | null;
  nextEventTime: number | null;
} {
  const epsilon =
    Number.isFinite(params.epsilon) && Number(params.epsilon) >= 0
      ? Number(params.epsilon)
      : 0.1;
  const previousBoundary = lowerBoundSortedNumbers(
    params.eventIndex,
    params.currentTime - epsilon,
  );
  const nextBoundary = upperBoundSortedNumbers(
    params.eventIndex,
    params.currentTime + epsilon,
  );

  return {
    previousEventCount: previousBoundary,
    nextEventCount: Math.max(0, params.eventIndex.length - nextBoundary),
    previousEventTime:
      previousBoundary > 0 ? params.eventIndex[previousBoundary - 1] : null,
    nextEventTime:
      nextBoundary < params.eventIndex.length
        ? params.eventIndex[nextBoundary]
        : null,
  };
}
